(function () {
  'use strict';

  function safeId(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\u0590-\u05ff\u0600-\u06ff]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function uniqueId(root, base, index) {
    let candidate = safeId(base) || `article-heading-${index + 1}`;
    let suffix = 1;

    while (document.getElementById(candidate)) {
      candidate = `${safeId(base) || `article-heading-${index + 1}`}-${suffix}`;
      suffix += 1;
    }

    return candidate;
  }

  function normalizeTocLabel(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/[\u200e\u200f]/g, '')
      .replace(/[.!?,:;\-–—]+$/g, '')
      .trim()
      .toLowerCase();
  }

  function collectTocTargets(root, toc) {
    const mode = toc.dataset.tocMode || 'both';
    const shouldDedupe = toc.dataset.tocDedupe !== 'false';
    const targets = [];
    const seenIds = new Set();
    const seenLabels = new Set();

    function addTarget(element, label, index) {
      if (!element || !label) return;

      const cleanLabel = String(label).trim();
      const normalizedLabel = normalizeTocLabel(cleanLabel);
      if (!normalizedLabel) return;
      if (shouldDedupe && seenLabels.has(normalizedLabel)) return;

      if (!element.id) {
        element.id = uniqueId(root, cleanLabel, index);
      }

      if (seenIds.has(element.id)) return;

      seenIds.add(element.id);
      if (shouldDedupe) seenLabels.add(normalizedLabel);
      targets.push({
        element,
        id: element.id,
        label: cleanLabel
      });
    }

    if (mode === 'blocks' || mode === 'both') {
      root.querySelectorAll('[data-toc-entry="true"]').forEach((element, index) => {
        const label =
          element.dataset.tocLabel ||
          element.querySelector('h1, h2, h3, h4')?.textContent ||
          '';

        addTarget(element, label, index);
      });
    }

    if (mode === 'headings' || mode === 'both') {
      root
        .querySelectorAll('[data-toc-scan-headings="true"] h2, [data-toc-scan-headings="true"] h3')
        .forEach((heading, index) => {
          addTarget(heading, heading.textContent || '', index + targets.length);
        });
    }

    return targets;
  }

  function initToc(root) {
    const toc = root.querySelector('[data-newton-toc]');
    const list = toc?.querySelector('[data-toc-list]');
    const toggle = toc?.querySelector('[data-toc-toggle]');

    if (!toc || !list || toc.dataset.tocInitialized === 'true') return;

    const targets = collectTocTargets(root, toc);
    list.innerHTML = '';

    targets.forEach((target) => {
      const item = document.createElement('li');
      const link = document.createElement('a');

      item.className = 'newton-article-toc__item';
      link.className = 'newton-article-toc__link';
      link.href = `#${target.id}`;
      link.textContent = target.label;
      link.dataset.targetId = target.id;

      item.appendChild(link);
      list.appendChild(item);
    });

    if (!targets.length) {
      toc.hidden = true;
      return;
    }

    toc.dataset.tocInitialized = 'true';

    const links = Array.from(list.querySelectorAll('.newton-article-toc__link'));

    /*
      The TOC must expand naturally with all generated entries.
      The old inner overflow scrollbar is disabled; the vertical indicator
      now represents the reader's progress through the article content.
    */
    list.classList.remove('is-scrollable');
    list.style.removeProperty('--nab-toc-list-max-height');

    const activeById = new Map(links.map((link) => [link.dataset.targetId, link]));
    let manualActiveUntil = 0;
    let scrollFrame = 0;

    function getScrollOffset() {
      const value = getComputedStyle(root).getPropertyValue('--nab-scroll-offset');
      return Number.parseFloat(value) || 0;
    }

    function setActiveLink(link) {
      links.forEach((currentLink) => {
        const isActive = currentLink === link;
        currentLink.classList.toggle('is-active', isActive);

        if (isActive) {
          currentLink.setAttribute('aria-current', 'location');
        } else {
          currentLink.removeAttribute('aria-current');
        }
      });
    }

    function syncTocProgress(activeLink) {
      const currentLink =
        activeLink ||
        list.querySelector('.newton-article-toc__link.is-active') ||
        links[0];
      const currentItem = currentLink?.closest('.newton-article-toc__item');

      if (!currentItem) return;

      const listStyle = window.getComputedStyle(list);
      const paddingTop = Number.parseFloat(listStyle.paddingTop) || 0;
      const paddingBottom = Number.parseFloat(listStyle.paddingBottom) || 0;
      const thumbHeight = 48;
      const trackHeight = Math.max(0, list.clientHeight - paddingTop - paddingBottom);
      const maxTravel = Math.max(0, trackHeight - thumbHeight);

      /*
        Move the progress thumb to the currently active TOC item instead of
        calculating it from raw page-scroll percentage. This keeps heading 7
        aligned with item 7 even when article sections have different heights.
      */
      const itemCenter = currentItem.offsetTop + currentItem.offsetHeight / 2;
      const wantedPosition = itemCenter - paddingTop - thumbHeight / 2;
      const progressY = Math.min(maxTravel, Math.max(0, wantedPosition));

      list.style.setProperty('--nab-toc-progress-y', `${Math.round(progressY)}px`);
    }

    function syncActiveFromScroll() {
      scrollFrame = 0;

      if (performance.now() < manualActiveUntil) return;

      const activationLine = getScrollOffset() + 24;
      let activeTarget = targets[0];

      targets.forEach((target) => {
        if (target.element.getBoundingClientRect().top <= activationLine) {
          activeTarget = target;
        }
      });

      const activeLink = activeById.get(activeTarget.id) || links[0];
      setActiveLink(activeLink);
      syncTocProgress(activeLink);
    }

    function requestActiveSync() {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(syncActiveFromScroll);
    }

    const hashTargetId = decodeURIComponent(window.location.hash.replace(/^#/, ''));
    const initialActiveLink = activeById.get(hashTargetId) || links[0];
    setActiveLink(initialActiveLink);
    window.requestAnimationFrame(() => syncTocProgress(initialActiveLink));

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready
        .then(() => syncTocProgress(
          list.querySelector('.newton-article-toc__link.is-active') || links[0]
        ))
        .catch(() => {});
    }

    list.addEventListener('click', function (event) {
      const link = event.target.closest('.newton-article-toc__link');
      if (!link) return;

      const target = document.getElementById(link.dataset.targetId);
      if (!target) return;

      event.preventDefault();

      setActiveLink(link);
      syncTocProgress(link);
      manualActiveUntil = performance.now() + 1200;

      const smoothAllowed =
        root.dataset.smoothScroll === 'true' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const targetTop =
        target.getBoundingClientRect().top +
        window.pageYOffset -
        getScrollOffset();

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: smoothAllowed ? 'smooth' : 'auto'
      });

      if (history.replaceState) {
        history.replaceState(null, '', `#${target.id}`);
      }

      window.setTimeout(requestActiveSync, smoothAllowed ? 1250 : 50);

      if (window.matchMedia('(max-width: 989px)').matches && toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        list.hidden = true;
      }
    });

    if (toggle) {
      toggle.addEventListener('click', function () {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        list.hidden = expanded;
      });
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(requestActiveSync, {
        rootMargin: '-15% 0px -70% 0px',
        threshold: [0, 0.1, 0.5]
      });

      targets.forEach((target) => observer.observe(target.element));
    }

    window.addEventListener('scroll', requestActiveSync, { passive: true });
    window.addEventListener('resize', requestActiveSync);
  }

  function setTocExpanded(toc, expanded) {
    const toggle = toc?.querySelector('[data-toc-toggle]');
    const list = toc?.querySelector('[data-toc-list]');

    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
    if (list) list.hidden = !expanded;
  }

  function initResponsivePlacement(root) {
    if (root.dataset.newtonPlacementInitialized === 'true') return;

    const media = window.matchMedia('(max-width: 989px)');
    const sidebar = root.querySelector('.newton-article-builder__sidebar');
    const main = root.querySelector('.newton-article-builder__main');
    const form = root.querySelector('[data-newton-enquiry]');
    const toc = root.querySelector('[data-newton-toc]');
    const desktopFormSlot = root.querySelector('[data-desktop-form-slot]');
    const desktopTocSlot = root.querySelector('[data-desktop-toc-slot]');
    const mobileFormSlot = root.querySelector('[data-mobile-form-slot]');
    const mobileTocSlot = root.querySelector('[data-mobile-toc-slot]');

    if (!sidebar || !main) return;

    function placeTools() {
      if (media.matches) {
        if (toc) {
          const tocHost = mobileTocSlot || main;
          tocHost.appendChild(toc);
          if (mobileTocSlot) {
            const mobileSticky = toc.dataset.mobileSticky === 'true';
            mobileTocSlot.classList.toggle('is-sticky', mobileSticky);
            mobileTocSlot.style.setProperty(
              '--nab-toc-mobile-top',
              `${parseInt(toc.dataset.mobileStickyTop || '88', 10) || 0}px`
            );
          }
          setTocExpanded(toc, false);
        }

        if (form) {
          (mobileFormSlot || main).appendChild(form);
        }
      } else {
        if (form && desktopFormSlot) desktopFormSlot.after(form);
        if (toc && desktopTocSlot) desktopTocSlot.after(toc);
        if (mobileTocSlot) mobileTocSlot.classList.remove('is-sticky');
        if (toc) setTocExpanded(toc, true);
      }
    }

    root.dataset.newtonPlacementInitialized = 'true';
    placeTools();

    if (media.addEventListener) {
      media.addEventListener('change', placeTools);
    } else {
      media.addListener(placeTools);
    }
  }

  function updateSliderButtons(slider, items, index, shouldLoop) {
    const previousButton = slider.parentElement?.querySelector('[data-slider-prev]');
    const nextButton = slider.parentElement?.querySelector('[data-slider-next]');

    if (shouldLoop) {
      const shouldDisable = items.length < 2;
      if (previousButton) previousButton.disabled = shouldDisable;
      if (nextButton) nextButton.disabled = shouldDisable;
      return;
    }

    if (previousButton) previousButton.disabled = index <= 0;
    if (nextButton) nextButton.disabled = index >= items.length - 1;
  }

  function initSlider(slider) {
    if (slider.dataset.sliderInitialized === 'true') return;

    const track = slider.querySelector('[data-slider-track]');
    if (!track) return;

    let items = Array.from(track.querySelectorAll('[data-slider-item]'));
    let currentIndex = 0;
    const shouldLoop = slider.classList.contains('newton-article-product-slider');
    const productControls = slider.querySelector('.newton-article-slider__controls--products');

    slider.dataset.sliderInitialized = 'true';

    function syncProductArrowPosition() {
      if (!shouldLoop || !productControls) return;

      const firstMedia = slider.querySelector('.newton-article-product-card__media');
      if (!firstMedia) return;

      const sliderRect = slider.getBoundingClientRect();
      const mediaRect = firstMedia.getBoundingClientRect();
      const arrowTop = mediaRect.top - sliderRect.top + (mediaRect.height / 2);

      if (arrowTop > 0) {
        productControls.style.setProperty('--nab-product-slider-arrow-top', arrowTop + 'px');
      }
    }

    function moveTo(index) {
      items = Array.from(track.querySelectorAll('[data-slider-item]'));
      if (!items.length) return;

      if (shouldLoop && items.length > 1) {
        currentIndex = ((index % items.length) + items.length) % items.length;
      } else {
        currentIndex = Math.max(0, Math.min(index, items.length - 1));
      }

      items[currentIndex].scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
      updateSliderButtons(slider, items, currentIndex, shouldLoop);
    }

    const controlsRoot = slider.parentElement || slider;
    controlsRoot.querySelector('[data-slider-prev]')?.addEventListener('click', function () {
      moveTo(currentIndex - 1);
    });
    controlsRoot.querySelector('[data-slider-next]')?.addEventListener('click', function () {
      moveTo(currentIndex + 1);
    });

    updateSliderButtons(slider, items, currentIndex, shouldLoop);

    if (shouldLoop) {
      requestAnimationFrame(syncProductArrowPosition);
      slider.querySelectorAll('.newton-article-product-card__image').forEach(function (image) {
        if (!image.complete) image.addEventListener('load', syncProductArrowPosition, { once: true });
      });
      window.addEventListener('resize', syncProductArrowPosition);
    }

    slider._newtonRefresh = function () {
      items = Array.from(track.querySelectorAll('[data-slider-item]'));
      currentIndex = 0;
      updateSliderButtons(slider, items, currentIndex, shouldLoop);
      requestAnimationFrame(syncProductArrowPosition);
    };
  }

  function createExternalReviewCard(review, direction) {
    const card = document.createElement('article');
    const top = document.createElement('div');
    const quote = document.createElement('span');
    const source = document.createElement('span');
    const stars = document.createElement('div');
    const body = document.createElement('div');
    const author = document.createElement('div');

    card.className = 'newton-article-review-card';
    card.dataset.sliderItem = '';
    card.dir = review.direction || direction || 'rtl';

    top.className = 'newton-article-review-card__top';
    quote.className = 'newton-article-review-card__quote';
    source.className = 'newton-article-review-card__source';
    stars.className = 'newton-article-review-card__stars';
    body.className = 'newton-article-review-card__body rte';
    author.className = 'newton-article-review-card__author';

    quote.textContent = '“';
    quote.setAttribute('aria-hidden', 'true');
    source.textContent = review.source_label || review.source || '';

    const rating = Math.max(0, Math.min(5, Number(review.rating) || 0));
    stars.setAttribute('aria-label', `${rating} out of 5`);

    for (let star = 1; star <= 5; star += 1) {
      const starElement = document.createElement('span');
      starElement.textContent = '★';
      starElement.setAttribute('aria-hidden', 'true');
      if (star > Math.round(rating)) starElement.classList.add('is-empty');
      stars.appendChild(starElement);
    }

    body.textContent = review.text || review.review_text || '';

    if (review.author_url) {
      const link = document.createElement('a');
      link.href = review.author_url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = review.author || review.author_name || '';
      author.appendChild(link);
    } else {
      const strong = document.createElement('strong');
      strong.textContent = review.author || review.author_name || '';
      author.appendChild(strong);
    }

    top.appendChild(quote);
    if (source.textContent) top.appendChild(source);
    card.appendChild(top);
    card.appendChild(stars);
    card.appendChild(body);
    card.appendChild(author);

    return card;
  }

  function getReviewArray(payload) {
    if (Array.isArray(payload)) return payload;

    const candidates = [
      payload?.reviews,
      payload?.google_reviews,
      payload?.items,
      payload?.results,
      payload?.data,
      payload?.data?.reviews,
      payload?.data?.items,
      payload?.result?.reviews,
      payload?.result?.items
    ];

    return candidates.find(Array.isArray) || [];
  }

  function normalizeExternalReview(review, fallbackSource) {
    const reviewer = review?.reviewer || review?.user || review?.author_details || {};

    return {
      direction: review?.direction || '',
      source_label:
        review?.source_label ||
        review?.source ||
        review?.provider ||
        fallbackSource ||
        'Google',
      rating: review?.rating ?? review?.stars ?? review?.score ?? 0,
      text:
        review?.text ||
        review?.review_text ||
        review?.comment ||
        review?.content ||
        review?.body ||
        '',
      author:
        review?.author ||
        review?.author_name ||
        review?.reviewer_name ||
        review?.profile_name ||
        reviewer?.name ||
        '',
      author_url:
        review?.author_url ||
        review?.review_url ||
        review?.url ||
        reviewer?.profile_url ||
        ''
    };
  }

  function renderReviewPayload(root, slider, payload) {
    const track = slider.querySelector('[data-review-track]');
    const status = root.querySelector('[data-review-status]');
    const ratingElement = root.querySelector('[data-external-rating]');
    const sourceElement = root.querySelector('[data-external-source]');
    const fallbackSource =
      payload?.source_label ||
      payload?.source ||
      payload?.provider ||
      payload?.data?.source_label ||
      'Google';
    const reviewLimit = Math.max(0, parseInt(root.dataset.reviewLimit || '0', 10) || 0);
    let reviews = getReviewArray(payload).map((review) =>
      normalizeExternalReview(review, fallbackSource)
    );

    if (reviewLimit > 0) reviews = reviews.slice(0, reviewLimit);

    if (!track || !reviews.length) {
      throw new Error('No reviews were returned');
    }

    track.innerHTML = '';
    reviews.forEach((review) => {
      track.appendChild(createExternalReviewCard(review, root.dir));
    });

    const payloadRating =
      payload?.rating ??
      payload?.average_rating ??
      payload?.data?.rating ??
      payload?.result?.rating;

    if (ratingElement && payloadRating != null && Number.isFinite(Number(payloadRating))) {
      ratingElement.textContent = Number(payloadRating).toFixed(1);
    }

    if (sourceElement && fallbackSource) {
      sourceElement.textContent = fallbackSource;
    }

    if (status) status.textContent = '';
    slider._newtonRefresh?.();
  }

  async function loadConfiguredReviews(root) {
    const reviewSections = root.querySelectorAll('.newton-article-reviews[data-review-source]');

    reviewSections.forEach(async (reviewRoot) => {
      const source = reviewRoot.dataset.reviewSource || 'manual';
      if (!['external', 'shop_metafield', 'article_metafield', 'custom_json'].includes(source)) return;

      const slider = reviewRoot.querySelector('[data-slider]');
      const status = reviewRoot.querySelector('[data-review-status]');
      if (!slider) return;

      try {
        if (status) status.textContent = source === 'external' ? 'Loading reviews…' : '';

        let payload;
        if (source === 'external') {
          const endpoint = slider.dataset.reviewEndpoint?.trim();
          if (!endpoint) throw new Error('Review endpoint is empty');

          const response = await fetch(endpoint, {
            method: 'GET',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          });

          if (!response.ok) {
            throw new Error(`Review endpoint returned ${response.status}`);
          }
          payload = await response.json();
        } else {
          const jsonScript = slider.querySelector('[data-review-json]');
          if (!jsonScript?.textContent?.trim()) {
            throw new Error('Review JSON is empty');
          }
          payload = JSON.parse(jsonScript.textContent);
        }

        renderReviewPayload(reviewRoot, slider, payload);
      } catch (error) {
        if (status) {
          status.textContent = 'Live reviews are unavailable. Showing saved reviews.';
        }
      }
    });
  }



  function initProductColorSwitchers(root) {
    root.querySelectorAll('[data-newton-product-card]').forEach((card) => {
      if (card.dataset.productColorsInitialized === 'true') return;
      card.dataset.productColorsInitialized = 'true';

      const media = card.querySelector('[data-newton-product-media]');
      const productImage = card.querySelector('.newton-article-product-card__image');
      const previousMedia = card.querySelector('[data-newton-product-media-previous]');
      const nextMedia = card.querySelector('[data-newton-product-media-next]');

      const parseMediaUrls = (value) =>
        String(value || '')
          .split('|||')
          .map((url) => url.trim())
          .filter(Boolean);

      const updateMediaArrowState = () => {
        if (!media) return;
        const hasMultipleImages = parseMediaUrls(media.dataset.mediaUrls).length > 1;
        [previousMedia, nextMedia].forEach((control) => {
          if (!control) return;
          control.classList.toggle('is-hidden', !hasMultipleImages);
          control.setAttribute('aria-hidden', hasMultipleImages ? 'false' : 'true');
          control.tabIndex = hasMultipleImages ? 0 : -1;
        });
      };

      const showMediaAt = (requestedIndex, imageAlt, animateFromRight) => {
        if (!media || !productImage) return;
        const mediaUrls = parseMediaUrls(media.dataset.mediaUrls);
        if (!mediaUrls.length) return;

        const index = (requestedIndex + mediaUrls.length) % mediaUrls.length;
        const imageUrl = mediaUrls[index];
        const preload = new Image();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const commitImage = () => {
          productImage.removeAttribute('srcset');
          productImage.src = imageUrl;
          if (imageAlt) productImage.alt = imageAlt;
          media.dataset.mediaIndex = String(index);
        };

        preload.onload = function () {
          if (!animateFromRight || reduceMotion) {
            commitImage();
            return;
          }

          if (media.dataset.mediaChanging === 'true') return;
          media.dataset.mediaChanging = 'true';
          productImage.classList.add('is-media-leaving-left');

          window.setTimeout(() => {
            commitImage();
            productImage.classList.remove('is-media-leaving-left');
            productImage.classList.add('is-media-entering-right');

            /* Force the incoming position before animating it to center. */
            void productImage.offsetWidth;

            window.requestAnimationFrame(() => {
              productImage.classList.remove('is-media-entering-right');

              window.setTimeout(() => {
                media.dataset.mediaChanging = 'false';
              }, 300);
            });
          }, 170);
        };
        preload.src = imageUrl;
      };

      const moveMedia = (direction) => {
        if (!media || media.dataset.mediaChanging === 'true') return;
        const currentIndex = Number.parseInt(media.dataset.mediaIndex || '0', 10) || 0;
        showMediaAt(currentIndex + direction, '', direction > 0);
      };

      const handleMediaControl = (event, direction) => {
        event.preventDefault();
        event.stopPropagation();
        moveMedia(direction);
      };

      if (previousMedia) {
        previousMedia.addEventListener('click', (event) => handleMediaControl(event, -1));
        previousMedia.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') handleMediaControl(event, -1);
        });
      }

      if (nextMedia) {
        nextMedia.addEventListener('click', (event) => handleMediaControl(event, 1));
        nextMedia.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') handleMediaControl(event, 1);
        });
      }

      updateMediaArrowState();

      card.addEventListener('click', function (event) {
        const swatch = event.target.closest('[data-newton-product-swatch]');
        if (!swatch || !card.contains(swatch)) return;

        event.preventDefault();
        event.stopPropagation();

        card.querySelectorAll('[data-newton-product-swatch]').forEach((button) => {
          const active = button === swatch;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });

        const productUrl = String(swatch.dataset.productUrl || '').trim();
        const compareUrl = String(swatch.dataset.productCompareUrl || '').trim();
        const modelTitle = String(swatch.dataset.productModelTitle || '').trim();
        const hebrewText = String(swatch.dataset.productHebrew || '').trim();
        const colorName = String(swatch.dataset.productColor || '').trim();
        const productPrice = String(swatch.dataset.productPrice || '').trim();
        const comparePrice = String(swatch.dataset.productCompare || '').trim();
        const hasCompare = swatch.dataset.productHasCompare === 'true' && !!comparePrice;
        const imageUrl = String(swatch.dataset.productImage || '').trim();
        const imageAlt = String(swatch.dataset.productImageAlt || modelTitle || colorName).trim();
        const productMedia = String(swatch.dataset.productMedia || imageUrl).trim();

        /* Compare button must keep pointing to the compare page, not the product page. */
        card.querySelectorAll(
          '.newton-article-product-card__media, .newton-article-product-card__title, .newton-article-product-card__button:not([data-newton-product-compare])'
        ).forEach((link) => {
          if (productUrl) link.setAttribute('href', productUrl);
        });

        const compareButton = card.querySelector('[data-newton-compare-button]');
        if (compareButton && compareUrl) {
          compareButton.setAttribute('href', compareUrl);
        }

        const title = card.querySelector('[data-newton-product-title]');
        if (title && modelTitle) title.textContent = modelTitle;

        const hebrew = card.querySelector('[data-newton-product-hebrew]');
        if (hebrew) {
          hebrew.textContent = hebrewText;
          hebrew.classList.toggle('is-empty', !hebrewText);
        }

        const color = card.querySelector('[data-newton-product-color]');
        if (color) color.textContent = colorName;

        const price = card.querySelector('[data-newton-product-price]');
        if (price && productPrice) price.textContent = productPrice;

        const compare = card.querySelector('[data-newton-product-compare]');
        if (compare) {
          compare.hidden = !hasCompare;
          compare.textContent = hasCompare ? comparePrice : '';
        }

        if (media && productMedia) {
          media.dataset.mediaUrls = productMedia;
          media.dataset.mediaIndex = '0';
          updateMediaArrowState();
          showMediaAt(0, imageAlt);
        } else if (productImage && imageUrl) {
          const preload = new Image();
          preload.onload = function () {
            productImage.removeAttribute('srcset');
            productImage.src = imageUrl;
            productImage.alt = imageAlt;
          };
          preload.src = imageUrl;
        }

        card.dataset.selectedProductId = String(swatch.dataset.productId || '');
      });
    });
  }

  function initFlashyEnquiry(root) {
    root.querySelectorAll('[data-newton-enquiry]').forEach((card) => {
      if (card.dataset.flashyInitialized === 'true') return;
      card.dataset.flashyInitialized = 'true';

      const form = card.querySelector('form');
      if (!form) return;

      const flashyEnabled = card.dataset.flashyEnabled === 'true';
      const nameInput = card.querySelector('[data-newton-enquiry-name]');
      const phoneInput = card.querySelector('[data-newton-enquiry-phone]');
      const submitButton = form.querySelector('button[type="submit"]');

      try {
        const cleanUrl = new URL(window.location.href);
        let changed = false;

        Array.from(cleanUrl.searchParams.keys()).forEach((key) => {
          if (
            key.startsWith('contact[') ||
            key === 'form_type' ||
            key === 'utf8' ||
            key === 'contact_posted'
          ) {
            cleanUrl.searchParams.delete(key);
            changed = true;
          }
        });

        if (changed && window.history?.replaceState) {
          window.history.replaceState(
            {},
            document.title,
            cleanUrl.pathname + cleanUrl.search + cleanUrl.hash
          );
        }
      } catch (error) {}

      form.querySelectorAll('.newton-article-enquiry__errors').forEach((node) => node.remove());

      let statusElement = form.querySelector('[data-newton-enquiry-status]');
      if (!statusElement) {
        statusElement = document.createElement('p');
        statusElement.dataset.newtonEnquiryStatus = 'true';
        statusElement.setAttribute('role', 'status');
        statusElement.setAttribute('aria-live', 'polite');
        statusElement.hidden = true;
        form.appendChild(statusElement);
      }

      function setStatus(type, message) {
        statusElement.hidden = !message;
        statusElement.textContent = message || '';
        statusElement.classList.remove(
          'newton-article-enquiry__success',
          'newton-article-enquiry__errors'
        );

        if (type === 'success') {
          statusElement.classList.add('newton-article-enquiry__success');
        } else if (type === 'error') {
          statusElement.classList.add('newton-article-enquiry__errors');
        }
      }

      function normalizePhone(value) {
        /*
          Flashy documents phone values as digits including the country code.
          Keep international numbers, convert 00-prefixes, and convert Israeli
          local numbers to 972 format. Bare 8-15 digit values are also allowed.
        */
        const rawPhone = String(value || '').trim();
        let digits = rawPhone.replace(/\D+/g, '');

        if (!digits) return '';
        if (digits.startsWith('00')) digits = digits.slice(2);
        if (digits.startsWith('0')) digits = `972${digits.replace(/^0+/, '')}`;

        return digits;
      }

      function isValidPhone(value) {
        return /^[1-9]\d{7,14}$/.test(normalizePhone(value));
      }

      function splitName(value) {
        const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
        return {
          firstName: parts.shift() || '',
          lastName: parts.join(' ')
        };
      }

      function waitForFlashyContacts(timeoutMs) {
        return new Promise((resolve, reject) => {
          const startedAt = Date.now();
          let intervalId = null;

          const cleanup = () => {
            if (intervalId) window.clearInterval(intervalId);
            window.removeEventListener('onFlashy', checkReady);
          };

          const checkReady = () => {
            const contacts = window.flashy && window.flashy.contacts;

            if (
              contacts &&
              (
                typeof contacts.createOrUpdate === 'function' ||
                typeof contacts.create === 'function'
              )
            ) {
              cleanup();
              resolve(contacts);
              return;
            }

            if (Date.now() - startedAt >= timeoutMs) {
              cleanup();
              reject(new Error('Flashy pixel is not ready'));
            }
          };

          window.addEventListener('onFlashy', checkReady);
          intervalId = window.setInterval(checkReady, 100);
          checkReady();
        });
      }

      function isDirectFlashyApiEndpoint(endpoint) {
        const endpointValue = String(endpoint || '').trim();

        return (
          !endpointValue ||
          /(^|\/\/)api\.flashy\.app\/contact(?:[/?#]|$)/i.test(endpointValue)
        );
      }

      async function sendWithFlashyPixel(contact, listId) {
        const contacts = await waitForFlashyContacts(8000);
        const validListId = Number.isFinite(listId) && listId > 0
          ? listId
          : undefined;
        const contactPayload = {
          phone: contact.phone,
          first_name: contact.first_name,
          last_name: contact.last_name
        };

        if (contact.source) {
          contactPayload.source = contact.source;
        }

        /*
          This form is phone-primary. Pass "phone" explicitly so Flashy does
          not fall back to its email-primary contact flow. No generated or
          hidden email address is sent.
        */
        if (typeof contacts.createOrUpdate === 'function') {
          return Promise.resolve(
            contacts.createOrUpdate(contactPayload, validListId, 'phone')
          );
        }

        if (typeof contacts.create === 'function') {
          return Promise.resolve(
            contacts.create(contactPayload, validListId, 'contact', 'phone')
          );
        }

        throw new Error('FLASHY_CONTACTS_API_UNAVAILABLE');
      }

      async function sendWithFlashyRest(contact, listId, endpoint, apiToken, accountId) {
        const endpointValue = String(endpoint || '').trim();

        /*
          Never call api.flashy.app directly from storefront JavaScript.
          That request is blocked by browser CORS and exposes the API token.
          This REST path is only for a custom backend / Shopify app-proxy URL.
        */
        if (isDirectFlashyApiEndpoint(endpointValue)) {
          throw new Error('DIRECT_FLASHY_REST_NOT_ALLOWED');
        }

        const contactPayload = { ...contact };

        if (Number.isFinite(listId) && listId > 0) {
          contactPayload.lists = { [String(listId)]: 1 };
        }

        const requestBody = {
          primary_key: 'phone',
          account_id: accountId || '',
          list_id: Number.isFinite(listId) ? listId : null,
          contact: contactPayload
        };

        const headers = {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        };

        /*
          Kept for compatibility with a custom backend endpoint.
          For best security, store the real Flashy token on the backend
          instead of exposing it in theme settings.
        */
        if (apiToken) {
          const cleanApiToken = String(apiToken)
            .trim()
            .split(/https?:\/\//i)[0]
            .replace(/\s+/g, '');

          if (cleanApiToken) {
            headers['x-api-key'] = cleanApiToken;
          }
        }

        const response = await fetch(endpointValue, {
          method: 'POST',
          credentials: 'same-origin',
          headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          let responseMessage = '';

          try {
            const responseData = await response.json();
            responseMessage =
              responseData?.message ||
              responseData?.error ||
              JSON.stringify(responseData);
          } catch (error) {
            try {
              responseMessage = await response.text();
            } catch (innerError) {}
          }

          throw new Error(
            `Flashy proxy request failed (${response.status}) ${responseMessage}`.trim()
          );
        }

        return response;
      }


      async function submitShopifyPhoneAjax(phone) {
        const formData = new FormData(form);
        formData.delete('contact[contact_detail]');
        formData.set('contact[phone]', phone);

        const response = await fetch(form.action || window.location.href, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`Shopify contact request failed (${response.status})`);
        }
      }

      form.addEventListener('submit', async function (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (form.dataset.flashySubmitting === 'true') return;

        const fullName = nameInput?.value?.trim() || '';
        const rawPhone = phoneInput?.value?.trim() || '';
        const normalizedPhone = normalizePhone(rawPhone);

        setStatus('', '');
        nameInput?.removeAttribute('aria-invalid');
        phoneInput?.removeAttribute('aria-invalid');

        if (nameInput?.required && !fullName) {
          nameInput.setAttribute('aria-invalid', 'true');
          nameInput.focus();
          setStatus('error', 'יש להזין שם מלא.');
          return;
        }

        if (!isValidPhone(rawPhone)) {
          phoneInput?.setAttribute('aria-invalid', 'true');
          phoneInput?.focus();
          setStatus('error', 'יש להזין מספר טלפון המכיל 8 עד 15 ספרות.');
          return;
        }

        const { firstName, lastName } = splitName(fullName);
        const listId = parseInt(String(card.dataset.flashyListId || '').trim(), 10);
        const endpoint = String(card.dataset.flashyEndpoint || '').trim();
        const apiToken = String(card.dataset.flashyApiToken || '').trim();
        const accountId = String(card.dataset.flashyAccountId || '').trim();
        const includeSource = card.dataset.flashyIncludeSource !== 'false';
        const source = includeSource
          ? (String(card.dataset.flashySource || '').trim() || 'Newton article enquiry')
          : '';

        const contact = {
          phone: normalizedPhone,
          first_name: firstName,
          last_name: lastName
        };

        if (source) contact.source = source;

        form.dataset.flashySubmitting = 'true';

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.setAttribute('aria-busy', 'true');
        }

        try {
          /*
            Flashy form is allowed to submit only when:
            1. Flashy integration is enabled.
            2. Flashy API token is entered in the block custom setting.

            The token acts as the required configuration gate. When the
            storefront uses the installed Flashy Pixel, the token is not sent
            to api.flashy.app, which avoids the browser CORS error.
          */
          if (!flashyEnabled) {
            throw new Error('FLASHY_INTEGRATION_DISABLED');
          }

          if (!apiToken) {
            throw new Error('FLASHY_API_TOKEN_MISSING');
          }

          const hasCustomProxyEndpoint =
            endpoint && !isDirectFlashyApiEndpoint(endpoint);

          if (hasCustomProxyEndpoint) {
            await sendWithFlashyRest(
              contact,
              listId,
              endpoint,
              apiToken,
              accountId
            );
          } else {
            await sendWithFlashyPixel(contact, listId);
          }

          /*
            Do not submit this phone-only form to Shopify's contact endpoint.
            Shopify validates contact[email] for contact forms, while this
            form intentionally contains only name and phone. Flashy is the
            destination for this enquiry.
          */

          form.reset();
          setStatus(
            'success',
            String(card.dataset.successMessage || '').trim() ||
              'תודה! נחזור אליכם בהקדם.'
          );

          document.dispatchEvent(
            new CustomEvent('newton:article-enquiry:success', {
              bubbles: true,
              detail: {
                contactType: 'phone',
                contactValue: normalizedPhone,
                source,
                listId: Number.isFinite(listId) ? listId : null
              }
            })
          );
        } catch (error) {
          console.error('Newton article enquiry submission error:', error);

          if (error?.message === 'FLASHY_API_TOKEN_MISSING') {
            setStatus('error', 'מפתח ה-API של Flashy חסר.');
          } else if (error?.message === 'FLASHY_INTEGRATION_DISABLED') {
            setStatus('error', 'אינטגרציית Flashy אינה פעילה.');
          } else if (error?.message === 'FLASHY_LIST_ID_MISSING') {
            setStatus('error', 'מזהה רשימת Flashy חסר או אינו תקין.');
          } else if (
            error?.message === 'FLASHY_CONTACTS_API_UNAVAILABLE' ||
            error?.message === 'Flashy pixel is not ready'
          ) {
            setStatus('error', 'Flashy עדיין לא נטען. נסו שוב בעוד רגע.');
          } else if (error?.message === 'DIRECT_FLASHY_REST_NOT_ALLOWED') {
            setStatus('error', 'יש להשתמש ב-Flashy Pixel או בכתובת שרת מתווך.');
          } else {
            setStatus('error', 'שליחת הטופס נכשלה. נסו שוב בעוד רגע.');
          }
        } finally {
          delete form.dataset.flashySubmitting;

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.removeAttribute('aria-busy');
          }
        }
      }, true);
    });
  }

  function initFaq(root) {
    root.querySelectorAll('[data-faq-accordion]').forEach((faqSection) => {
      const accordionEnabled = faqSection.dataset.faqAccordion === 'true';
      const onlyOneOpen = faqSection.dataset.faqSingle === 'true';

      if (!accordionEnabled) {
        faqSection.querySelectorAll('[data-faq-button]').forEach((button) => {
          button.setAttribute('aria-expanded', 'true');
          const answer = document.getElementById(button.getAttribute('aria-controls'));
          if (answer) answer.hidden = false;
        });
        return;
      }

      faqSection.addEventListener('click', function (event) {
        const button = event.target.closest('[data-faq-button]');
        if (!button || !faqSection.contains(button)) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        const answer = document.getElementById(button.getAttribute('aria-controls'));

        if (onlyOneOpen && !expanded) {
          faqSection.querySelectorAll('[data-faq-button][aria-expanded="true"]').forEach((openButton) => {
            openButton.setAttribute('aria-expanded', 'false');
            const openAnswer = document.getElementById(openButton.getAttribute('aria-controls'));
            if (openAnswer) openAnswer.hidden = true;
          });
        }

        button.setAttribute('aria-expanded', String(!expanded));
        if (answer) answer.hidden = expanded;
      });
    });
  }

  function initBuilder(root) {
    if (root.dataset.newtonInitialized === 'true') return;
    root.dataset.newtonInitialized = 'true';

    initResponsivePlacement(root);
    initToc(root);
    root.querySelectorAll('[data-slider]').forEach(initSlider);
    initProductColorSwitchers(root);
    initFaq(root);
    initFlashyEnquiry(root);
    loadConfiguredReviews(root);
  }

  function initAll(scope) {
    (scope || document)
      .querySelectorAll('[data-newton-article-builder]')
      .forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    const target = event.target.closest('[id]');
    if (!target) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  });
})();
